import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { UserSummary } from '@shared/types';
import { api } from '../api';

interface AuthValue {
  user: UserSummary | null;
  loading: boolean;
  login(input: { email: string; password: string }): Promise<void>;
  register(input: { name: string; email: string; password: string; publicSlug?: string }): Promise<void>;
  logout(): Promise<void>;
  refresh(): Promise<void>;
  setUser(user: UserSummary): void;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserSummary | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      setUser((await api.me()).data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void refresh(); }, []);

  const value = useMemo<AuthValue>(() => ({
    user,
    loading,
    setUser,
    refresh,
    login: async (input) => setUser((await api.login(input)).data),
    register: async (input) => setUser((await api.register(input)).data),
    logout: async () => { await api.logout(); setUser(null); }
  }), [user, loading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('AuthProvider ausente.');
  return value;
}
